import { KeyboardEvent, ReactNode } from "react";

import { ExplanationTooltip } from "@/components/_molecules/explanation-tooltip";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { ModuleAction, ProductModule } from "@ark/shared";

import { useCanModuleAction } from "../hooks/use-can-module-action";

interface ModuleActionGuardProps {
  module: ProductModule;
  action: ModuleAction;
  children: ReactNode;
}

// Wraps a create/delete control so a member whose seat tier lacks the capability
// sees it disabled with an explaining tooltip rather than a control that fails on
// click. Members who may perform the action see the control unchanged. The server
// enforces regardless — this is UX only.
export const ModuleActionGuard = ({ module, action, children }: ModuleActionGuardProps) => {
  const { t } = useDomainTranslation("common");
  const canPerform = useCanModuleAction({ module, action });

  if (canPerform) {
    return <>{children}</>;
  }

  const tooltip = t("moduleAccess.guestCannotCreate");

  // Neutralise activation from every source: pointer-events on the child kills
  // mouse/touch, and the capture-phase key handler stops keyboard Enter/Space
  // before it activates a focusable button or link inside (which is what would
  // otherwise synthesise a click the disabling can't reach).
  const blockActivation = (event: KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <ExplanationTooltip content={tooltip}>
      <span
        className="inline-flex cursor-not-allowed opacity-50"
        aria-disabled="true"
        onKeyDownCapture={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            blockActivation(event);
          }
        }}
      >
        <span className="pointer-events-none">{children}</span>
      </span>
    </ExplanationTooltip>
  );
};
